import { createAuthClient } from "better-auth/react";
import { twoFactorClient } from "better-auth/client/plugins";
import { clearPersistedData } from "./persistence";
import { eraseAllDrafts } from "@/features/chat/draftPersistence";

/**
 * better-auth React client. Same-origin (rides the Vite dev proxy in dev and
 * the Worker-served SPA in prod) so cookies stay first-party. The client
 * requires an ABSOLUTE base URL — a relative one throws at module init.
 */
export const authClient = createAuthClient({
  baseURL: `${window.location.origin}/api/auth`,
  plugins: [
    twoFactorClient({
      onTwoFactorRedirect: () => {
        // Handled explicitly by the login flow (we inspect signIn's
        // response for `twoFactorRedirect` and navigate ourselves), so this
        // is just a safety net if some other call trips 2FA.
        if (location.pathname !== "/two-factor") {
          location.assign("/two-factor");
        }
      },
    }),
  ],
});

export type Session = ReturnType<typeof authClient.useSession>["data"];

/**
 * Whether a session check that named no user failed to ask rather than
 * answered. better-auth's client reports an unreachable server or a 5xx the
 * same way as "signed out" — no user — and the app used to treat both as a
 * sign-out: wipe this browser's cache and go to /login. Only an answer is a
 * sign-out: no session (`null`), or a 401.
 */
export function sessionCheckUnanswered(error: unknown): boolean {
  if (error == null) return false;
  return (error as { status?: unknown }).status !== 401;
}

export { authErrorMessage } from "./auth-errors";

/**
 * Sign out and land on the login page — whether or not the server agreed.
 *
 * A rejected `signOut` almost always means the session was already gone (an
 * expired cookie, a revoked session, an offline tab). Leaving the person parked
 * on a workspace they can no longer load would be the worst reading of that, so
 * the redirect happens either way and `/login` re-establishes the truth.
 */
export function signOutAndRedirect(): void {
  const leave = () => { window.location.href = "/login"; };
  // Nothing unsent outlives the account on this browser either.
  eraseAllDrafts();
  // Nothing of this account stays in the browser's cache after it signs out.
  void clearPersistedData()
    .catch(() => undefined)
    .then(() => authClient.signOut())
    .then(leave, leave);
}
